<template>
  <v-filter-modal
    class="customer-filters-mobile"
    :active-menu-item.sync="activeMenuItem"
    :menu="$options.NAME_MENU_ITEM"
    :count="filtersCount"
    @apply="applyFilters"
    @reset="resetFilters"
    @close="closeFilters"
  >
    <div v-if="!activeMenuItem" class="customer-filters-mobile__sorts">
      <div class="customer-filters-mobile__tabs-wrapper-title">СОРТИРОВАТЬ ПО</div>
      <div class="customer-filters-mobile__radio-wrapper" body-scroll-lock-ignore>
        <v-radio
          v-for="option in $options.SORT_OPTIONS"
          :key="option.label"
          v-model="filtersParams.orderBy"
          class="customer-filters-mobile__radio"
          :value="option.value"
          >{{ option.label }}</v-radio
        >
      </div>
    </div>

    <div class="customer-filters-mobile__menu">
      <v-menu v-if="!activeMenuItem" v-model="activeMenuItem">
        <v-menu-item name="category">
          Категория
          <span class="customer-filters-mobile__menu-item-preview"> {{ preview.category }} </span>
        </v-menu-item>
        <v-menu-item name="price">
          Цена
          <span class="customer-filters-mobile__menu-item-preview"> {{ pricePreview }} </span>
        </v-menu-item>
        <v-menu-item v-if="isRoleAdmin" name="agent">
          Агент
          <span class="customer-filters-mobile__menu-item-preview">
            {{ preview.agent }}
          </span>
        </v-menu-item>
        <v-menu-item name="date">
          Дата добавления
          <span class="owners-filters-mobile__menu-item-preview"> {{ datePreview }} </span>
        </v-menu-item>
        <v-menu-item name="lastReminder">
          Дата последней задачи
          <span class="owners-filters-mobile__menu-item-preview"> {{ lastReminderPreview }} </span>
        </v-menu-item>
        <v-menu-item name="status">
          Статус
          <span class="customer-filters-mobile__menu-item-preview"> {{ preview.status }} </span>
        </v-menu-item>
      </v-menu>
    </div>

    <div v-if="!agentOptionsLoading" class="customer-filters-mobile__sections-wrapper">
      <div v-show="activeMenuItem === 'category'" class="customer-filters-mobile__select">
        <filters-select
          v-model="selectedCategory"
          :options="categoryOptions"
          class="customer-filters-mobile__list-item"
        />
      </div>
      <div v-show="activeMenuItem === 'agent'" class="customer-filters-mobile__select">
        <filters-select v-model="selectedAgent" :options="agentOptions" class="customer-filters-mobile__list-item" />
      </div>
      <div v-show="activeMenuItem === 'status'" class="customer-filters-mobile__select">
        <filters-select v-model="selectedStatus" :options="statusOptions" class="customer-filters-mobile__list-item" />
      </div>

      <v-filter-price
        v-show="activeMenuItem === 'price'"
        :price-gte.sync="filtersParams.priceGte"
        :price-lte.sync="filtersParams.priceLte"
      />
      <v-date-publication
        v-show="activeMenuItem === 'date'"
        :date-from.sync="filtersParams.dateFrom"
        :date-to.sync="filtersParams.dateTo"
      />
      <v-date-publication
        v-show="activeMenuItem === 'lastReminder'"
        :date-from.sync="filtersParams.lastReminderFrom"
        :date-to.sync="filtersParams.lastReminderTo"
      />
    </div>
  </v-filter-modal>
</template>

<script>
import VFilterModal from '@/components/common/VFilterModal.vue'
import VMenu from '@/components/common/VMenu.vue'
import VMenuItem from '@/components/common/VMenuItem.vue'
import VRadio from '@/components/common/VRadio.vue'
import FiltersSelect from '@/components/Agent/AgentsSelect.vue'
import { advertsService, agentsService, customersService } from '@/services/http'
import { SORT_OPTIONS_MAP } from '@/constants/customersOptions'
import { pluralizeNumeral } from '@/utils/pluralization'
import { formatDatePreview, formatMoney } from '@/utils/formatters'
import { mapGetters } from 'vuex'
import { MODULE_SESSION } from '@/store/modules/session/session.types'
import VFilterPrice from '@/components/common/VFilterPrice.vue'
import VDatePublication from '@/components/common/VDatePublication.vue'

function generateFilters() {
  return {
    categoryId: undefined,
    agentId: undefined,
    orderBy: '-created_at',
    dateTo: undefined,
    dateFrom: undefined,
    lastReminderFrom: undefined,
    lastReminderTo: undefined,
    priceLte: undefined,
    priceGte: undefined,
    workflowStatus: undefined
  }
}

function generatePreview() {
  return {
    agent: null,
    category: null,
    status: null
  }
}

const NAME_MENU_ITEM_MAP = {
  agent: 'Агент',
  price: 'Цена',
  category: 'Категории',
  date: 'Дата добавления',
  lastReminder: 'Дата последней задачи',
  status: 'Статус'
}

export default {
  SORT_OPTIONS: SORT_OPTIONS_MAP,
  NAME_MENU_ITEM: NAME_MENU_ITEM_MAP,
  name: 'CustomerFiltersMobile',
  components: {
    VDatePublication,
    VFilterPrice,
    VFilterModal,
    VRadio,
    VMenu,
    VMenuItem,
    FiltersSelect
  },
  props: {
    count: {
      type: Number,
      default: 0
    },
    defaultFilters: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      agentOptions: [],
      categoryOptions: [],
      statusOptions: [],
      activeMenuItem: undefined,
      agentOptionsLoading: false,
      categoryOptionsLoading: false,
      statusOptionsLoading: false,
      filtersParams: generateFilters(),
      preview: generatePreview()
    }
  },
  computed: {
    ...mapGetters({
      isRoleAdmin: `${MODULE_SESSION}/isRoleAdmin`
    }),
    selectedCategory: {
      get() {
        return { id: this.filtersParams.categoryId }
      },
      set(category) {
        this.filtersParams.categoryId = category?.id
        this.preview.category = category?.name
      }
    },
    selectedAgent: {
      get() {
        const a = this.agentOptions.find(agent => agent.profileId === this.filtersParams.agentId)?.id
        return { id: a }
      },
      set(agent) {
        this.filtersParams.agentId = agent?.profileId
        this.preview.agent = agent?.name
      }
    },
    selectedStatus: {
      get() {
        return { id: this.filtersParams.workflowStatus }
      },
      set(status) {
        this.filtersParams.workflowStatus = status?.id
        this.preview.status = status?.name
      }
    },
    pricePreview() {
      if (this.filtersParams.priceGte || this.filtersParams.priceLte) {
        return `${formatMoney(this.filtersParams.priceGte) || '0'} - ${formatMoney(this.filtersParams.priceLte) ||
          '...'} ₽`
      }
      return null
    },
    datePreview() {
      return formatDatePreview(this.filtersParams.dateFrom, this.filtersParams.dateTo)
    },
    lastReminderPreview() {
      return formatDatePreview(this.filtersParams.lastReminderFrom, this.filtersParams.lastReminderTo)
    },
    filtersCount() {
      const text = pluralizeNumeral(this.count, 'объект', 'объекта', 'объектов')
      return `${this.count} ${text}`
    }
  },
  watch: {
    defaultFilters: {
      immediate: true,
      handler({ orderBy, status, page, ...newFilters }) {
        this.filtersParams = { ...this.filtersParams, ...newFilters }
      }
    },
    agentOptions() {
      this.preview.agent = this.agentOptions.find(agent => agent.profileId === this.filtersParams.agentId)?.name
    },
    categoryOptions() {
      this.preview.category = this.categoryOptions.find(category => category.id === this.filtersParams.categoryId)?.name
    },
    statusOptions() {
      this.preview.status = this.statusOptions.find(status => status.id === this.filtersParams.workflowStatus)?.name
    }
  },
  created() {
    if (this.isRoleAdmin) {
      this.selectAgents()
    }
    this.selectCategory()
    this.selectWorkflowStatus()
  },
  methods: {
    selectWorkflowStatus() {
      this.statusOptionsLoading = true
      return customersService
        .getCustomerStatusesMap()
        .then(statuses => {
          this.statusOptions = statuses
        })
        .finally(() => {
          this.statusOptionsLoading = false
        })
    },
    selectAgents() {
      this.agentOptionsLoading = true
      return agentsService
        .select()
        .then(agentList => {
          this.agentOptions = agentList
        })
        .finally(() => {
          this.agentOptionsLoading = false
        })
    },
    selectCategory() {
      this.categoryOptionsLoading = true
      return advertsService
        .selectCategory()
        .then(categories => {
          this.categoryOptions = categories
        })
        .finally(() => {
          this.categoryOptionsLoading = false
        })
    },
    changeOrder(orderBy) {
      this.filtersParams.orderBy = orderBy
    },
    closeFilters() {
      this.$emit('close')
    },
    resetFilters() {
      this.filtersParams = generateFilters()
      this.preview = generatePreview()
    },
    applyFilters() {
      this.$emit('apply-filters', this.filtersParams)
    }
  }
}
</script>
